import { useCallback, useMemo, useState } from 'react';

import { AlertCircleIcon, ArrowLeftIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router';
import { useTitle } from 'react-use';

import { PageSubHeader, PageSubHeaderBreadcrumbs } from '@/components/organisms/PageSubHeader';
import PageContentWrapper from '@/components/templates/PageContentWrapper';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { useModalTryIt } from '@/hooks/useModalTryIt';
import { selectAccountSlug } from '@/redux/session/selectors';
import { trackEvent } from '@/segment/segment';
import useSegments from '@/segment/useSegments';
import { EventName, PageName } from '@/shared/types/segment';

import { CampaignWrapper } from '../../components/CampaignWrapper';
import { ResumeSubscriberModal } from '../../components/modals/ResumeSubscriberModal';
import { StopSubscriberModal } from '../../components/modals/StopSubscriberModal';
import { ActivityTimeline } from '../../components/SubscriberJourney/ActivityTimeline';
import {
  ContactInfoCard,
  getJourneySessionUser,
} from '../../components/SubscriberJourney/ContactInfoCard';
import { CurrentStatusCard } from '../../components/SubscriberJourney/CurrentStatusCard';
import { NextActionsSection } from '../../components/SubscriberJourney/NextActionsSection';
import { OriginalDebtCard } from '../../components/SubscriberJourney/OriginalDebtCard';
import { SubscriberJourneyHeader } from '../../components/SubscriberJourney/SubscriberJourneyHeader';
import { SubscriberJourneySkeleton } from '../../components/SubscriberJourney/SubscriberJourneySkeleton';
import { useCampaigns } from '../../hooks/useCampaigns';
import { useCampaignSubscriber } from '../../hooks/useCampaignSubscriber';
import { useCampaignSubscribers } from '../../hooks/useCampaignSubscribers';
import {
  getSubscriberStopInfo,
  isSubscriberResumable,
  shouldShowStoppedBadge,
} from '../../util/getSubscriberStopInfo';

export const SubscriberJourney = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { campaignId, subscriberId } = useParams<{ campaignId: string; subscriberId: string }>();
  const slug = useSelector(selectAccountSlug);
  const [urlParams] = useSearchParams();
  const sessionIdFromUrl = urlParams.get('session_id');
  const { handleAccountReplayClick } = useModalTryIt(sessionIdFromUrl);

  const [showStopModal, setShowStopModal] = useState(false);
  const [showResumeModal, setShowResumeModal] = useState(false);

  const { campaign } = useCampaigns(campaignId);
  const { subscriber, isLoading, status } = useCampaignSubscriber(campaignId, subscriberId);
  const { campaign_subscribers, totalRows, hasNextPage, fetchNextPage } =
    useCampaignSubscribers(campaignId);

  useTitle(t('pages.campaigns_subscriber_journey', { 0: campaign?.name ?? '-' }));

  useSegments(PageName.CAMPAIGN_SUBSCRIBER_JOURNEY);

  const currentIndex = useMemo(() => {
    if (!subscriberId || !campaign_subscribers.length) {
      return -1;
    }
    return campaign_subscribers.findIndex((s) => s.subscriber_id === subscriberId);
  }, [subscriberId, campaign_subscribers]);

  const contactName = useMemo(() => {
    if (!subscriber) {
      return undefined;
    }
    const ctx = subscriber.context as Record<string, unknown> | undefined;
    const user = ctx?.user as Record<string, unknown> | undefined;
    return (user?.display_name as string) || subscriber.phone || subscriber.email || undefined;
  }, [subscriber]);

  const stopInfo = useMemo(
    () => (subscriber ? getSubscriberStopInfo(subscriber, subscriber.contact_journey) : null),
    [subscriber]
  );

  const handleSessionClick = useCallback(
    (sessionId: string) => {
      handleAccountReplayClick(sessionId);
    },
    [handleAccountReplayClick]
  );

  const logsBasePath = `/${slug}/campaigns/${campaignId}/logs`;

  const handleNavigate = useCallback(
    async (direction: 'prev' | 'next') => {
      trackEvent(EventName.CampaignJourneyNavigate, { campaign_id: campaignId, direction });
      const nextIndex = direction === 'prev' ? currentIndex - 1 : currentIndex + 1;
      let nextSubscriber = campaign_subscribers[nextIndex];
      if (!nextSubscriber && direction === 'next' && hasNextPage) {
        const result = await fetchNextPage();
        const fetched = result.data?.pages.flatMap((page) => page.subscribers) ?? [];
        nextSubscriber = fetched[nextIndex];
      }
      if (nextSubscriber) {
        navigate(`${logsBasePath}/${nextSubscriber.subscriber_id}`);
      }
    },
    [
      currentIndex,
      campaign_subscribers,
      navigate,
      logsBasePath,
      campaignId,
      hasNextPage,
      fetchNextPage,
    ]
  );

  return (
    <>
      <PageSubHeader
        breadcrumbs={
          <PageSubHeaderBreadcrumbs>
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <div className="flex items-center">
                    <Link to={logsBasePath}>
                      <Button variant="ghost-neutral" size="icon-sm">
                        <ArrowLeftIcon size={16} color="var(--sidebar-foreground)" />
                      </Button>
                    </Link>
                    <div className="mr-4 ml-2 h-4 w-px bg-border" />
                    <BreadcrumbLink asChild>
                      <Link to={`/${slug}/campaigns`} className="breadcrumb-link">
                        {t('common.campaigns')}
                      </Link>
                    </BreadcrumbLink>
                  </div>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to={logsBasePath} className="breadcrumb-link">
                      {campaign?.name}
                    </Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage>{contactName || subscriberId}</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </PageSubHeaderBreadcrumbs>
        }
      />

      <PageContentWrapper>
        <CampaignWrapper>
          {isLoading ? (
            <SubscriberJourneySkeleton />
          ) : status === 'error' || !subscriber ? (
            <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
              <AlertCircleIcon className="h-10 w-10 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                {t('campaigns.subscriber_journey.unknown')}
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-6 lg:flex-row lg:gap-12">
              {/* Left column */}
              <div className="flex flex-1 flex-col gap-6">
                <SubscriberJourneyHeader
                  subscriber={subscriber}
                  currentIndex={currentIndex >= 0 ? currentIndex : undefined}
                  totalCount={totalRows ?? campaign_subscribers.length}
                  onNavigate={handleNavigate}
                />
                <NextActionsSection
                  journey={subscriber.contact_journey}
                  lifecycleStatus={subscriber.lifecycle_status}
                  campaignStatus={campaign?.status}
                  isStopped={shouldShowStoppedBadge(stopInfo)}
                  isResumable={isSubscriberResumable(stopInfo)}
                  isAgentic={!!campaign?.nba_config}
                  nextAction={subscriber.next_action}
                  timeZone={
                    (getJourneySessionUser(subscriber.contact_journey).timezone as
                      string | undefined) ?? subscriber.context?.time_zone
                  }
                  campaignId={campaignId}
                  subscriberId={subscriberId}
                  onStop={() => {
                    trackEvent(EventName.CampaignStopSubscriber, { campaign_id: campaignId });
                    setShowStopModal(true);
                  }}
                  onResume={() => {
                    trackEvent(EventName.CampaignResumeSubscriber, { campaign_id: campaignId });
                    setShowResumeModal(true);
                  }}
                />
                <ActivityTimeline
                  subscriber={subscriber}
                  contactName={contactName}
                  onSessionClick={handleSessionClick}
                  stopInfo={stopInfo}
                />
              </div>

              {/* Right column */}
              <div className="flex w-full flex-col gap-4 lg:w-80">
                {campaign?.type === 'payment_recovery' && (
                  <CurrentStatusCard subscriber={subscriber} />
                )}
                <ContactInfoCard subscriber={subscriber} />
                {campaign?.type === 'payment_recovery' && (
                  <OriginalDebtCard subscriber={subscriber} />
                )}
              </div>
            </div>
          )}
        </CampaignWrapper>
      </PageContentWrapper>

      {showStopModal && campaignId && subscriberId && (
        <StopSubscriberModal
          campaignId={campaignId}
          subscriberId={subscriberId}
          onClose={() => setShowStopModal(false)}
        />
      )}

      {showResumeModal && campaignId && subscriberId && (
        <ResumeSubscriberModal
          campaignId={campaignId}
          subscriberId={subscriberId}
          onClose={() => setShowResumeModal(false)}
        />
      )}
    </>
  );
};
